import { type ReactElement, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Switch } from '@fastnorth/kraken/components/input';
import FollowedLimitDialog from './FollowedLimitDialog';
import { useSearchParams } from 'react-router-dom';
import { MAX_FOLLOWERS_PER_CLIENT } from '../../views/following/constants';
import { useToast } from '@fastnorth/kraken/components/toast';

type FollowToggleProps = {
  id: string;
  participantName: string;
  hideLabel?: boolean;
  trackingLocation: 'participant_summary_details' | 'participant_details' | 'leaderboard_table';
};

const FollowToggle = ({
  id,
  participantName,
  hideLabel = false,
  trackingLocation,
}: FollowToggleProps): ReactElement => {
  const { t } = useTranslation('translation', { keyPrefix: 'karwa.components.search_results' });
  const [showModal, setShowModal] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const followsFromUrl = searchParams.getAll('follows');
  const isFollowed = followsFromUrl.includes(id);
  const { toast } = useToast();

  const onCheckedChange = useCallback(
    (checked: boolean) => {
      if (checked && followsFromUrl.length >= MAX_FOLLOWERS_PER_CLIENT) {
        setShowModal(true);
        return;
      }

      if (checked) {
        searchParams.append('follows', id);
        toast({
          title: t('following_toast.title', { count: followsFromUrl.length + 1 }),
          variant: 'success',
          description: t('following_toast.description'),
        });
      } else {
        searchParams.delete('follows', id);
        if (searchParams.get('active') === id) {
          searchParams.delete('active');
        }
      }
      setSearchParams(searchParams);
    },
    [followsFromUrl.length, setSearchParams, searchParams, id, toast, t]
  );

  return (
    <>
      <Switch
        id={id}
        hideLabel={hideLabel}
        label={t('switch_follow_label')}
        align={'center'}
        onCheckedChange={onCheckedChange}
        checked={isFollowed}
        data-dd-action-name={`switch-follow_toggle-${trackingLocation}`}
      />
      <FollowedLimitDialog
        open={showModal}
        onOpenChange={setShowModal}
        participantName={participantName}
      />
    </>
  );
};

FollowToggle.displayName = 'FollowToggle';

export default FollowToggle;
